/**
 * Utilidades para jerarquía de fechas y filtros dinámicos temporales estilo Power BI.
 * Jerarquía: Año (YYYY) -> Mes (Mes / MM) -> Inicio de Semana (Lunes DD/MM/YYYY según ISO-8601).
 */

export interface DateHierarchyParts {
    year: string;               // e.g. "2026"
    month: string;              // e.g. "Marzo"
    monthKey: string;           // e.g. "2026-03"
    monthNumber: string;        // e.g. "03"
    weekStart: string;          // e.g. "2026-03-02"
    weekStartLabel: string;     // e.g. "Lunes 02/03/2026"
    dateISO: string;            // e.g. "2026-03-05"
    dayLabel: string;           // e.g. "05/03/2026"
}

const MONTH_NAMES_ES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAY_NAMES_ES = [
    'Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'
];

/**
 * Parsea de manera robusta una fecha (string 'YYYY-MM-DD', 'DD/MM/YYYY', ISO o timestamp) a Date local seguro.
 */
export function parseDateSafe(rawDate: any): Date | null {
    if (!rawDate) return null;
    if (rawDate instanceof Date && !isNaN(rawDate.getTime())) return rawDate;

    const str = String(rawDate).trim();
    if (!str || str === '-' || str.toLowerCase() === 'sin fecha' || str.toLowerCase() === 'null') return null;

    // Formato DD/MM/YYYY o DD-MM-YYYY
    const ddmmyyyy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (ddmmyyyy) {
        const day = parseInt(ddmmyyyy[1], 10);
        const month = parseInt(ddmmyyyy[2], 10) - 1;
        const year = parseInt(ddmmyyyy[3], 10);
        const d = new Date(year, month, day, 12, 0, 0);
        return isNaN(d.getTime()) ? null : d;
    }

    // Formato YYYY-MM-DD
    const yyyymmdd = str.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
    if (yyyymmdd) {
        const year = parseInt(yyyymmdd[1], 10);
        const month = parseInt(yyyymmdd[2], 10) - 1;
        const day = parseInt(yyyymmdd[3], 10);
        const d = new Date(year, month, day, 12, 0, 0);
        return isNaN(d.getTime()) ? null : d;
    }

    const parsed = new Date(str);
    return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Obtiene la fecha exacta del Lunes de esa semana según ISO-8601.
 */
export function getISOWeekMonday(date: Date): Date {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
    const day = d.getDay(); // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
    // Distancia al lunes anterior: si es domingo (0), retroceder 6 días; si es lunes (1), retroceder 0 días; etc.
    const diff = (day === 0 ? -6 : 1 - day);
    d.setDate(d.getDate() + diff);
    return d;
}

/**
 * Descompone cualquier timestamp o fecha en sus niveles jerárquicos estándar.
 */
export function getDateHierarchyParts(rawDate: any): DateHierarchyParts | null {
    const d = parseDateSafe(rawDate);
    if (!d) return null;

    const year = d.getFullYear().toString();
    const monthIdx = d.getMonth();
    const month = MONTH_NAMES_ES[monthIdx];
    const monthNumber = String(monthIdx + 1).padStart(2, '0');
    const monthKey = `${year}-${monthNumber}`;

    const monday = getISOWeekMonday(d);
    const mYear = monday.getFullYear();
    const mMonth = String(monday.getMonth() + 1).padStart(2, '0');
    const mDay = String(monday.getDate()).padStart(2, '0');
    const weekStart = `${mYear}-${mMonth}-${mDay}`;
    const weekStartLabel = `Lunes ${mDay}/${mMonth}/${mYear}`;

    const dYear = d.getFullYear();
    const dMonth = String(d.getMonth() + 1).padStart(2, '0');
    const dDay = String(d.getDate()).padStart(2, '0');
    const dateISO = `${dYear}-${dMonth}-${dDay}`;
    const dayLabel = `${dDay}/${dMonth}/${dYear}`;

    return {
        year,
        month,
        monthKey,
        monthNumber,
        weekStart,
        weekStartLabel,
        dateISO,
        dayLabel
    };
}

/**
 * Encuentra automáticamente el primer campo de fecha/tiempo válido en una fila.
 */
export function extractRowDate(row: any): Date | null {
    if (!row || typeof row !== 'object') return null;

    // Prioridad por nombres conocidos de columnas temporales en K-LyDApp
    const candidateKeys = [
        'Fecha de Inicio', 'Fecha', 'Fecha Inicio', 'startDate', 'Fecha Mod',
        'Fecha Requerida', 'Fecha Termino', 'Fecha de Termino', 'endDate',
        'date', 'created_at', 'creationDate'
    ];

    for (const k of candidateKeys) {
        if (row[k] !== undefined && row[k] !== null && row[k] !== '') {
            const d = parseDateSafe(row[k]);
            if (d) return d;
        }
    }

    // Búsqueda en cualquier propiedad que contenga 'fecha' o 'date'
    for (const key of Object.keys(row)) {
        const lower = key.toLowerCase();
        if ((lower.includes('fecha') || lower.includes('date')) && !lower.includes('atraso') && !lower.includes('diff')) {
            const val = row[key];
            const d = parseDateSafe(val);
            if (d) return d;
        }
    }

    return null;
}

/**
 * Formatea cualquier fecha, ISO o string al estándar estricto chileno/obra: dd-mm-aaaa.
 */
export function formatDateDDMMYYYY(rawDate: any): string {
    const d = parseDateSafe(rawDate);
    if (!d) return String(rawDate ?? '');
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
}

/**
 * Detecta si un string o valor representa una fecha válida y lo retorna formateado en dd-mm-aaaa.
 */
export function formatIfDate(val: any): string {
    if (val === undefined || val === null || val === '') return '(Vacío)';
    const str = String(val).trim();
    if (str === '-' || str === '(Vacío)' || str.toLowerCase() === 'sin fecha') return str;

    // Si ya coincide con YYYY-MM-DD o DD/MM/YYYY o ISO
    if (/^\d{4}[/-]\d{1,2}[/-]\d{1,2}/.test(str) || /^\d{1,2}[/-]\d{1,2}[/-]\d{4}/.test(str)) {
        const d = parseDateSafe(str);
        if (d) {
            return formatDateDDMMYYYY(d);
        }
    }
    return str;
}
